import { useMutation } from "@apollo/client";
import {
  Button,
  Group,
  Input,
  LoadingOverlay,
  ScrollArea,
  Select,
  TextInput,
  Textarea,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { showNotification } from "@mantine/notifications";
import { IconDeviceFloppy } from "@tabler/icons";
import { UPDATE_LOAN_PROVIDER } from "apollo/mutuations";
import { LOAN_PROVIDERS } from "apollo/queries";
import { customLoader } from "components/utilities/loader";
import { useEffect } from "react";

const LoanProviderEditModal = ({ setOpenedEdit, editData }) => {
  const form = useForm({
    initialValues: {
      name: "",
      code: "",
      description: "",
      logo_url: "",
      contact_email: "",
      contact_phone: "",
      website: "",
      api_base_url: "",
      status: 1,
    },
    validate: {
      name: (value) => (value ? null : "Name is required"),
      code: (value) => (value ? null : "Code is required"),
      contact_email: (value) =>
        value && !/^\S+@\S+$/.test(value) ? "Invalid email" : null,
      contact_phone: (value) =>
        value && !/^\d{8}$/.test(value) ? "Phone must be 8 digits" : null,
      website: (value) =>
        value && !/^https?:\/\/.+/.test(value)
          ? "Invalid URL (must start with http:// or https://)"
          : null,
      api_base_url: (value) =>
        value && !/^https?:\/\/.+/.test(value)
          ? "Invalid URL (must start with http:// or https://)"
          : null,
    },
  });

  useEffect(() => {
    if (editData) {
      // Strip +2519 prefix if it exists to show only the 8 digits
      let phoneNumber = editData.contact_phone || "";
      if (phoneNumber.startsWith("+2519")) {
        phoneNumber = phoneNumber.substring(5); // Remove +2519
      }

      form.setValues({
        name: editData.name || "",
        code: editData.code || "",
        description: editData.description || "",
        logo_url: editData.logo_url || "",
        contact_email: editData.contact_email || "",
        contact_phone: phoneNumber,
        website: editData.website || "",
        api_base_url: editData.api_base_url || "",
        status: editData.status || 1,
      });
    }
  }, [editData]);

  const [updateLoanProvider, { loading }] = useMutation(UPDATE_LOAN_PROVIDER, {
    refetchQueries: [
      {
        query: LOAN_PROVIDERS,
      },
    ],
    onCompleted() {
      showNotification({
        color: "green",
        title: "Success",
        message: "Loan Provider Updated Successfully",
      });
      setOpenedEdit(false);
    },
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message:
          error?.graphQLErrors?.[0]?.message ||
          "Failed to Update Loan Provider",
      });
    },
  });

  const handleSubmit = (values) => {
    const fullPhoneNumber = values.contact_phone
      ? `+2519${values.contact_phone}`
      : null;

    updateLoanProvider({
      variables: {
        id: editData.id,
        name: values.name,
        code: values.code,
        description: values.description || null,
        logo_url: values.logo_url || null,
        contact_email: values.contact_email || null,
        contact_phone: fullPhoneNumber,
        website: values.website || null,
        api_base_url: values.api_base_url || null,
        status: parseInt(values.status),
      },
    });
  };

  const statusOptions = [
    { value: 1, label: "Active" },
    { value: 0, label: "Inactive" },
  ];

  return (
    <div style={{ position: "relative", minHeight: "400px" }}>
      <LoadingOverlay
        visible={loading}
        color="blue"
        overlayBlur={2}
        loader={customLoader}
      />
      <div style={{ marginBottom: "20px" }}>
        <h2 style={{ margin: 0, marginBottom: "8px" }}>Edit Loan Provider</h2>
        <p style={{ margin: 0, color: "#666", fontSize: "14px" }}>
          Update the loan provider information. All fields marked with * are
          required.
        </p>
      </div>
      <ScrollArea style={{ height: "70vh" }} type="auto" offsetScrollbars>
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <TextInput
            label="Provider Name"
            placeholder="Enter provider name"
            required
            {...form.getInputProps("name")}
            mb="md"
          />

          <TextInput
            label="Provider Code"
            placeholder="Enter provider code"
            required
            {...form.getInputProps("code")}
            mb="md"
          />

          <Textarea
            label="Description"
            placeholder="Enter description"
            minRows={3}
            {...form.getInputProps("description")}
            mb="md"
          />

          <TextInput
            label="Logo URL"
            placeholder="https://example.com/logo.png"
            {...form.getInputProps("logo_url")}
            mb="md"
          />

          <TextInput
            label="Contact Email"
            placeholder="contact@example.com"
            {...form.getInputProps("contact_email")}
            mb="md"
          />

          <Input.Wrapper label="Contact Phone" mb="md">
            <div style={{ display: "flex", alignItems: "center" }}>
              <div
                style={{
                  backgroundColor: "#f1f3f5",
                  border: "1px solid #ced4da",
                  borderRight: "none",
                  padding: "8px 12px",
                  borderRadius: "4px 0 0 4px",
                  color: "#495057",
                  fontWeight: 500,
                  fontSize: "14px",
                }}
              >
                +2519
              </div>
              <Input
                placeholder="11000001"
                maxLength={8}
                {...form.getInputProps("contact_phone")}
                style={{ flex: 1 }}
                styles={{
                  input: {
                    borderRadius: "0 4px 4px 0",
                  },
                }}
              />
            </div>
            {form.errors.contact_phone && (
              <div
                style={{ color: "#fa5252", fontSize: "12px", marginTop: "5px" }}
              >
                {form.errors.contact_phone}
              </div>
            )}
          </Input.Wrapper>

          <TextInput
            label="Website"
            placeholder="https://www.example.com"
            {...form.getInputProps("website")}
            mb="md"
          />

          <TextInput
            label="API Base URL"
            placeholder="https://api.example.com"
            {...form.getInputProps("api_base_url")}
            mb="md"
          />

          <Select
            label="Status"
            placeholder="Select status"
            data={statusOptions}
            {...form.getInputProps("status")}
            mb="md"
          />

          <Group position="right" mt="xl">
            <Button
              type="submit"
              leftIcon={<IconDeviceFloppy size={16} />}
              style={{ backgroundColor: "#FF6A00", color: "#FFFFFF" }}
              disabled={loading}
            >
              Update
            </Button>
          </Group>
        </form>
      </ScrollArea>
    </div>
  );
};

export default LoanProviderEditModal;
